/**
 * TileEntities — Persistent data for blocks that have extra state
 * (chests, furnaces, beds, signs, etc.)
 *
 * Stored as a Map<"x,y,z", data> and persisted via localStorage.
 */

let STORAGE_KEY = 'voxelgame_tileentities';

// Loot tables for dungeon/mineshaft chests
const DUNGEON_LOOT = [
  { itemId: -34, weight: 10, min: 1, max: 4 },  // Iron Ingot
  { itemId: -35, weight: 5,  min: 1, max: 3 },  // Gold Ingot
  { itemId: -12, weight: 3,  min: 1, max: 2 },  // Diamond
  { itemId: -13, weight: 12, min: 2, max: 8 },  // Coal
  { itemId: -32, weight: 10, min: 1, max: 3 },  // Bread
  { itemId: -33, weight: 8,  min: 1, max: 2 },  // Apple
  { itemId: -14, weight: 6,  min: 2, max: 6 },  // Redstone
  { itemId: -16, weight: 2,  min: 1, max: 1 },  // Emerald
  { itemId: -19, weight: 8,  min: 1, max: 3 },  // Bone
  { itemId: -18, weight: 8,  min: 1, max: 3 },  // String
  { blockId: 12, weight: 6,  min: 2, max: 8 },  // Planks
  { blockId: 27, weight: 4,  min: 1, max: 4 },  // Iron Block
];

function _rollLoot(slots, itemCount) {
  const totalWeight = DUNGEON_LOOT.reduce((s, e) => s + e.weight, 0);
  const usedSlots = new Set();

  for (let i = 0; i < itemCount; i++) {
    let slotIdx;
    do { slotIdx = Math.floor(Math.random() * 27); } while (usedSlots.has(slotIdx));
    usedSlots.add(slotIdx);

    // Weighted random selection
    let roll = Math.random() * totalWeight;
    let entry = DUNGEON_LOOT[0];
    for (const e of DUNGEON_LOOT) {
      roll -= e.weight;
      if (roll <= 0) { entry = e; break; }
    }

    const count = entry.min + Math.floor(Math.random() * (entry.max - entry.min + 1));
    if (entry.blockId !== undefined) {
      slots[slotIdx] = { blockId: entry.blockId, count };
    } else {
      slots[slotIdx] = { itemId: entry.itemId, count };
    }
  }
}

export class TileEntities {
  constructor(slot) {
    this.data = new Map();
    this.world = null;
    if (slot !== undefined) {
      STORAGE_KEY = `voxelgame_te_${slot}`;
    }
    this._load();
  }

  /**
   * Set a reference to the World so we can check adjacent blocks for double chests.
   * @param {Object} world - the World object with getBlock(x,y,z)
   */
  setWorld(world) {
    this.world = world;
  }

  _key(x, y, z) {
    return `${x},${y},${z}`;
  }

  // --- Chest operations ---

  getChest(x, y, z) {
    const key = this._key(x, y, z);
    let chest = this.data.get(key);
    if (!chest || chest.type !== 'chest') {
      chest = { type: 'chest', slots: new Array(27).fill(null) };
      // Underground chests (Y < 45) = dungeon loot
      if (y < 45) {
        _rollLoot(chest.slots, 3 + Math.floor(Math.random() * 5)); // 3-7 items
      }
      this.data.set(key, chest);
      this._save();
    }
    return chest;
  }

  /**
   * Get a double chest if an adjacent chest exists, otherwise return a single chest.
   * When two chests are adjacent (+-x or +-z), they merge into a 54-slot chest.
   * The merged slots array is shared: the "primary" chest (lower x, or lower z) owns
   * the first 27 slots, the "secondary" chest owns the next 27.
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @returns {{ x, y, z, slots: Array, isDouble: boolean, x2?: number, z2?: number }}
   */
  getDoubleChest(x, y, z) {
    // CHEST block ID = 30
    const CHEST_ID = 30;

    // Check adjacent positions for another chest block
    const adjacentOffsets = [
      [1, 0], [-1, 0], [0, 1], [0, -1]
    ];

    let neighborX = null, neighborZ = null;

    if (this.world) {
      for (const [dx, dz] of adjacentOffsets) {
        const nx = x + dx;
        const nz = z + dz;
        const blockId = this.world.getBlock(nx, y, nz);
        if (blockId === CHEST_ID) {
          neighborX = nx;
          neighborZ = nz;
          break;
        }
      }
    }

    // No adjacent chest found -- return single chest
    if (neighborX === null) {
      const chest = this.getChest(x, y, z);
      return { x, y, z, slots: chest.slots, isDouble: false };
    }

    // Determine primary (lower coordinate) and secondary chest
    // Primary is the one with lower x, or if same x, lower z
    let px, pz, sx, sz;
    if (x < neighborX || (x === neighborX && z < neighborZ)) {
      px = x; pz = z; sx = neighborX; sz = neighborZ;
    } else {
      px = neighborX; pz = neighborZ; sx = x; sz = z;
    }

    // Get or create both chests
    const primaryChest = this.getChest(px, y, pz);
    const secondaryChest = this.getChest(sx, y, sz);

    // Merge into 54-slot array: primary slots first, then secondary
    const mergedSlots = new Array(54);
    for (let i = 0; i < 27; i++) {
      mergedSlots[i] = primaryChest.slots[i];
      mergedSlots[i + 27] = secondaryChest.slots[i];
    }

    // Create a proxy-like object that syncs changes back to both chests
    const doubleChest = {
      x: px, y, z: pz,
      x2: sx, z2: sz,
      isDouble: true,
      slots: mergedSlots,
      // Sync merged slots back to individual chests
      _syncBack: () => {
        for (let i = 0; i < 27; i++) {
          primaryChest.slots[i] = mergedSlots[i];
          secondaryChest.slots[i] = mergedSlots[i + 27];
        }
      }
    };

    return doubleChest;
  }

  removeChest(x, y, z) {
    this.data.delete(this._key(x, y, z));
    this._save();
  }

  // --- Bed operations ---

  setBedSpawn(x, y, z) {
    // Only one bed spawn at a time
    for (const [key, val] of this.data) {
      if (val.type === 'bed') this.data.delete(key);
    }
    this.data.set(this._key(x, y, z), { type: 'bed' });
    this._save();
  }

  getBedSpawn() {
    for (const [key, val] of this.data) {
      if (val.type === 'bed') {
        const [x, y, z] = key.split(',').map(Number);
        return { x, y: y + 1, z }; // Spawn above bed
      }
    }
    return null;
  }

  removeBed(x, y, z) {
    this.data.delete(this._key(x, y, z));
    this._save();
  }

  // --- Furnace operations ---

  getFurnace(x, y, z) {
    const key = this._key(x, y, z);
    let furnace = this.data.get(key);
    if (!furnace || furnace.type !== 'furnace') {
      furnace = {
        type: 'furnace', input: null, fuel: null, output: null,
        progress: 0, fuelLeft: 0, fuelMax: 0, smeltTime: 0,
      };
      this.data.set(key, furnace);
    }
    return furnace;
  }

  setFurnace(x, y, z, data) {
    this.data.set(this._key(x, y, z), data);
  }

  /** Get all active furnaces for smelting tick */
  getAllFurnaces() {
    const result = [];
    for (const [key, val] of this.data) {
      if (val.type === 'furnace') {
        const [x, y, z] = key.split(',').map(Number);
        result.push({ x, y, z, data: val });
      }
    }
    return result;
  }

  // --- Grave operations ---

  saveGrave(x, y, z) {
    const key = this._key(x, y, z);
    this.data.set(key, { type: 'grave', x, y, z, created: Date.now() });
    this._save();
  }

  getGrave(x, y, z) {
    const key = this._key(x, y, z);
    const g = this.data.get(key);
    return (g && g.type === 'grave') ? g : null;
  }

  listGraves() {
    const graves = [];
    for (const [key, val] of this.data) {
      if (val.type === 'grave') {
        const [x, y, z] = key.split(',').map(Number);
        graves.push({ x, y, z, created: val.created });
      }
    }
    // Sort by creation time (newest first)
    graves.sort((a, b) => (b.created || 0) - (a.created || 0));
    return graves;
  }

  removeGrave(x, y, z) {
    const key = this._key(x, y, z);
    const g = this.data.get(key);
    if (g && g.type === 'grave') {
      this.data.delete(key);
      this._save();
    }
  }

  // --- Persistence ---

  save(chestData) {
    // If a double chest was passed, sync its merged slots back first
    if (chestData && chestData.isDouble && chestData._syncBack) {
      chestData._syncBack();
    }
    this._save();
  }

  _save() {
    try {
      const obj = {};
      for (const [key, val] of this.data) {
        obj[key] = val;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      // localStorage full or unavailable
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      for (const [key, val] of Object.entries(obj)) {
        this.data.set(key, val);
      }
    } catch (e) {
      // corrupt data
    }
  }

  // Remove tile entity data when its block is broken
  onBlockRemoved(x, y, z) {
    const key = this._key(x, y, z);
    if (this.data.has(key)) {
      this.data.delete(key);
      this._save();
    }
  }
}
